import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, Inbox, LayoutTemplate, MailPlus, MessageCircleQuestion } from 'lucide-react';
import { listMyCampaigns, type Campaign } from '../../lib/supabase';
import { STAND_META, standVon, type Stand } from '../../lib/freigabe';
import { vorlage } from '../../lib/mailBuilder/vorlagen';
import { formatDateTime } from '../../lib/format';
import { rollenText } from '../../lib/rollen';
import { sportschulenFuerStudios } from '../../lib/sportschulen';
import { useAuthStore } from '../../stores/authStore';
import { FreigabeBadge } from '../../components/Freigabe';
import './Start.css';

function gruss(name: string): string {
  const h = new Date().getHours();
  const vorname = name.trim().split(/\s+/)[0];
  const n = vorname ? `, ${vorname}` : '';
  if (h < 11) return `Guten Morgen${n}`;
  if (h < 18) return `Hallo${n}`;
  return `Guten Abend${n}`;
}

const BEI_MIR: Stand[] = ['entwurf', 'rueckfrage'];
const BEI_REDAKTION: Stand[] = ['eingereicht', 'in_pruefung', 'freigegeben'];
const UNTERWEGS: Stand[] = ['geplant', 'wird_versendet'];

/** Startseite für Geschäftsführung und Studioleitung. */
export function Start() {
  const user = useAuthStore((s) => s.user);
  const mails = useQuery({ queryKey: ['my-campaigns'], queryFn: listMyCampaigns });
  const liste = mails.data ?? [];
  const nach = (st: Stand[]) => liste.filter((c) => st.includes(standVon(c)));
  const rueckfragen = liste.filter((c) => standVon(c) === 'rueckfrage');
  const schulen = sportschulenFuerStudios(user?.studios ?? []).map((s) => s.name);

  const spalte = (titel: string, text: string, eintraege: Campaign[], leer: string) => (
    <div className="st-spalte">
      <div className="st-spalte-kopf"><strong>{titel}</strong><span className="st-zahl">{eintraege.length}</span></div>
      <p className="st-spalte-text">{text}</p>
      {eintraege.length === 0 ? <p className="st-leer">{leer}</p> : (
        <ul>
          {eintraege.slice(0, 4).map((c) => (
            <li key={c.id}><Link to={`/meine-mails?mail=${c.id}`}>{c.original_subject || c.subject || c.name}</Link></li>
          ))}
          {eintraege.length > 4 && <li className="st-mehr"><Link to="/meine-mails">und {eintraege.length - 4} weitere</Link></li>}
        </ul>
      )}
    </div>
  );

  return (
    <div className="start">
      <div className="page-header">
        <div>
          <h1 className="page-title">{gruss(user?.fullName ?? '')}</h1>
          <p className="page-subtitle">
            {rollenText(user)}{schulen.length ? ` · ${schulen.join(', ')}` : ''} — Mails an Deine Sportschule, vorbereitet in wenigen Minuten, geprüft von der Redaktion.
          </p>
        </div>
      </div>

      {rueckfragen.length > 0 && (
        <div className="st-rueckfrage" role="status">
          <MessageCircleQuestion size={18} aria-hidden="true" />
          <div>
            <strong>Die Redaktion hat {rueckfragen.length === 1 ? 'eine Rückfrage' : `${rueckfragen.length} Rückfragen`}.</strong>
            {' '}{rueckfragen.map((c, i) => (
              <span key={c.id}>{i > 0 && ', '}<Link to={`/meine-mails?mail=${c.id}`}>„{c.original_subject || c.subject || c.name}"</Link></span>
            ))}
          </div>
        </div>
      )}

      <div className="st-kacheln" data-tour="start-kacheln">
        <Link to="/neue-mail" className="st-kachel st-kachel--haupt">
          <MailPlus size={26} aria-hidden="true" />
          <strong>Neue Mail</strong>
          <span>Vorlage wählen und los — Schritt für Schritt.</span>
        </Link>
        <Link to="/meine-mails" className="st-kachel">
          <Inbox size={22} aria-hidden="true" />
          <strong>Meine Mails</strong>
          <span>Entwürfe, Eingereichtes, Verschicktes.</span>
        </Link>
        <Link to="/vorlagen" className="st-kachel">
          <LayoutTemplate size={22} aria-hidden="true" />
          <strong>Vorlagen</strong>
          <span>So sehen die fertigen Mails aus.</span>
        </Link>
        <Link to="/handbuch" className="st-kachel">
          <BookOpen size={22} aria-hidden="true" />
          <strong>So funktioniert's</strong>
          <span>Ablauf, Vorlauf, häufige Fragen.</span>
        </Link>
      </div>

      <div className="st-wartet card" data-tour="start-wartet">
        <h2 className="card-title">Was wartet worauf</h2>
        <div className="st-spalten">
          {spalte('Bei Dir', 'Entwürfe und Rückfragen — hier bist Du dran.', nach(BEI_MIR), 'Nichts offen.')}
          {spalte('Bei der Redaktion', 'Eingereicht, in Prüfung oder gerade freigegeben.', nach(BEI_REDAKTION), 'Gerade wartet nichts.')}
          {spalte('Geplant', 'Freigegeben und für den Versand eingeplant.', nach(UNTERWEGS), 'Nichts geplant.')}
        </div>
      </div>

      <div className="card" data-tour="start-liste">
        <div className="card-header">
          <h2 className="card-title">Deine letzten Mails</h2>
          <Link to="/meine-mails" className="btn btn-ghost btn-sm">Alle anzeigen</Link>
        </div>
        {mails.isLoading ? <div className="loading-spinner" style={{ margin: '1.5rem auto' }} /> : liste.length === 0 ? (
          <div className="st-erste">
            <p><strong>Noch keine Mail?</strong> So geht's: Vorlage wählen → ein paar Fragen vorab → Felder ausfüllen → Empfänger wählen → Testmail → einreichen. Die Redaktion gibt frei, dann geht die Mail zu Deinem Wunschtermin raus.</p>
            <Link to="/neue-mail" className="btn btn-primary"><MailPlus size={16} aria-hidden="true" /> Erste Mail vorbereiten</Link>
          </div>
        ) : (
          <ul className="st-liste">
            {liste.slice(0, 5).map((c) => {
              const v = vorlage(c.builder_template);
              const s = standVon(c);
              return (
                <li key={c.id}>
                  <Link to={`/meine-mails?mail=${c.id}`} className="st-zeile">
                    <FreigabeBadge campaign={c} titel={false} />
                    <span className="st-zeile-text">
                      <strong>{c.original_subject || c.subject || c.name}</strong>
                      <span>{v ? `${v.emoji} ${v.name}` : 'Kampagne'}{c.desired_send_at ? ` · Wunsch ${formatDateTime(c.desired_send_at)}` : ''}</span>
                    </span>
                    <span className="st-wer">{STAND_META[s].wer !== '—' ? `${STAND_META[s].wer} ${STAND_META[s].wer === 'Du' ? 'bist' : 'ist'} dran` : ''}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
